import React, { Component } from 'react';
import { graphql } from '@apollo/client/react/hoc';
import { gql } from '@apollo/client';
import {
  CONTENTFUL_NEWS_PAGE_SIZE,
  fetchAllNewsItemsPages,
  shouldPaginateNewsItems,
} from '../../utils/contentfulNewsPagination';
import { filterPublicGenericPageWithNews } from '../../utils/publicRoutes';

export default function searchWithData(WrappedComponent) {
  const QUERY = gql`
    query searchPageQuery($newsSkip: Int, $newsLimit: Int) {
      # Department pages (active pages)
      genericPageWithNews {
        __typename
        route
        quickLinks {
          __typename
          route
          text
        }
        popularDocuments {
          __typename
          text
          fileLink {
            __typename
            title
            description
            url
          }
        }
        department {
          __typename
          route
          name
          visibilityValue
        }
      }

      # File listing pages + their documents
      genericPageListingOfFiles {
        __typename
        route
        filesAndDocuments {
          __typename
          visibilityValue
          department
          title
          uploadDate
          fileUpload {
            __typename
            url
          }
        }
        seasonalFilesAndDocuments {
          __typename
          visibilityValue
          department
          title
          uploadDate
          fileUpload {
            __typename
            url
          }
        }
      }

      # Search page chrome
      searchPages {
        __typename
        route
        backgroundImage {
          __typename
          title
          description
          url
        }
      }

      # Contentful news (department news pages)
      newsItems(skip: $newsSkip, limit: $newsLimit) {
        __typename
        visibilityValue
        newsTitle
        route
        date
        description
        department {
          __typename
          visibilityValue
          name
          route
        }
        image {
          __typename
          title
          description
          url
        }
      }

      _newsItemsMeta {
        count
      }

      exploreComponents {
        __typename
        isNotDisplayed
        exploreTitle
        exploreTile {
          __typename
          backgroundImage {
            __typename
            title
            description
            url
          }
          route
          title
          ctaText
        }
      }

      globalSites {
        __typename
        defaultImage {
          __typename
          title
          description
          url
        }
        feedback {
          __typename
          feedbackHeader
          feedbackDescription
          feedbackCtAtext
          feedbackImage {
            __typename
            title
            description
            url
          }
        }
        mainNavigation {
          __typename
          footerHeader
          footerBody
          copyrightHolder
          primaryNavigationLinks {
            __typename
            route
            text
          }
          seasonalPrimaryNavigationLinks {
            __typename
            route
            text
          }
          footerLinks {
            __typename
            route
            text
          }
          seasonalFooterLinks {
            __typename
            route
            text
          }
          secondaryFooterLinks {
            __typename
            route
            text
          }
          footerLogo {
            __typename
            title
            description
            url
          }
        }
        cubsLogo {
          __typename
          title
          url
        }
      }
    }
  `;

  const HOC = class extends Component {
    componentDidMount() {
      this.loadRemainingNewsItems();
    }

    componentDidUpdate(prevProps) {
      if (prevProps.searchPageQuery !== this.props.searchPageQuery) {
        this.loadRemainingNewsItems();
      }
    }

    componentWillUnmount() {
      this.unmounted = true;
    }

    async loadRemainingNewsItems() {
      const { searchPageQuery } = this.props;

      if (this.loadingRemainingNews || !shouldPaginateNewsItems(searchPageQuery)) {
        return;
      }

      this.loadingRemainingNews = true;

      try {
        await fetchAllNewsItemsPages(searchPageQuery);
      } catch (error) {
        if (!this.unmounted) {
          console.warn('Unable to fetch all Contentful news items for searchPageQuery.', error);
        }
      } finally {
        this.loadingRemainingNews = false;
      }
    }

    render() {
      return (
        <WrappedComponent
          {...this.props}
          searchPageQuery={filterPublicGenericPageWithNews(this.props.searchPageQuery)}
        />
      );
    }
  };

  return graphql(QUERY, {
    name: 'searchPageQuery',
    options: {
      variables: {
        newsSkip: 0,
        newsLimit: CONTENTFUL_NEWS_PAGE_SIZE,
      },
      notifyOnNetworkStatusChange: true,
    },
  })(HOC);
}
